import { useMutation } from "@tanstack/react-query";
import {
  createFileRoute,
  Outlet,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { type } from "arktype";
import { PlusCircle } from "lucide-react";
import { Suspense } from "react";
import { invitesValidator } from "shared";
import { toast } from "sonner";
import {
  type InviteRole,
  MemberInviteField,
} from "@/components/fields/member-invite-field";
import { NavigationTabs } from "@/components/navigation-tabs";
import { Page } from "@/components/page";
import { SheetSkeleton } from "@/components/skeletons/sheet-skeleton";
import { Button } from "@/components/ui/button";
import { Dismissible } from "@/components/ui/dismissible";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useAppForm } from "@/hooks/useCreateForm";
import { useRequiredRole } from "@/hooks/useRequiredRole";

const validateSearch = type({
  "invite?": "boolean",
});

export const Route = createFileRoute("/_authed/_org/organization/people")({
  component: RouteComponent,
  validateSearch,
});

function RouteComponent() {
  const search = Route.useSearch();
  const location = useLocation();
  const navigate = Route.useNavigate();

  const { admin, viewOnlyAdmin } = useRequiredRole();

  if (location.pathname === "/organization/people") {
    return navigate({
      to: "/organization/people/all",
    });
  }

  return (
    <Page
      heading="People"
      actions={
        admin && (
          <Button
            onClick={() => {
              navigate({
                search: {
                  invite: true,
                },
                to: location.pathname,
              });
            }}
          >
            <PlusCircle />
            Invite User
          </Button>
        )
      }
      tabs={
        <NavigationTabs
          tabs={[
            {
              icon: "users",
              label: "People",
              to: "/organization/people/all",
            },
            {
              icon: "user-plus",
              label: "Invites",
              to: "/organization/people/invitations",
              visible: viewOnlyAdmin,
            },
          ]}
        />
      }
    >
      <Sheet
        onOpenChange={(open) => {
          if (!open) {
            navigate({
              search: {
                invite: open,
              },
              to: location.pathname,
            });
          }
        }}
        open={search.invite}
      >
        <Outlet />

        <SheetContent className="w-full">
          <Suspense
            fallback={
              <div className="px-6">
                <SheetSkeleton />
              </div>
            }
          >
            <InviteUserSheet />
          </Suspense>
        </SheetContent>
      </Sheet>
    </Page>
  );
}

function InviteUserSheet() {
  const navigate = useNavigate();

  const { queryClient, trpc } = Route.useRouteContext();
  const { mutate: sendInvites, isPending } = useMutation(
    trpc.org.sendInvites.mutationOptions({
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess: (response) => {
        toast.success(`${response.length} invites sent`);
        queryClient.invalidateQueries(trpc.org.invites.queryOptions());
        navigate({
          to: "/organization/people/invitations",
        });
      },
    }),
  );
  const form = useAppForm({
    defaultValues: {
      invites: [{ email: "", role: "member" as InviteRole }],
    },
    onSubmit: ({ value }) => {
      sendInvites({
        invites: value.invites,
      });
    },
    validators: {
      onSubmit: invitesValidator,
    },
  });
  return (
    <div className="flex h-full flex-col">
      <SheetHeader className="pb-2">
        <SheetTitle>Invite Members</SheetTitle>
        <SheetDescription>
          An email will be sent to their address inviting them to join the
          organization. The role determines what permissions the user will have
          on the platform.{" "}
          <a
            href="https://docs.givefront.com/member-roles"
            target="_blank"
            rel="noopener"
            className="text-accent-foreground hover:underline"
          >
            More info on Givefront roles ↗
          </a>
        </SheetDescription>
      </SheetHeader>

      <form
        className="space-y-6 overflow-y-auto p-4"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <div className="grow py-2">
          <MemberInviteField
            form={form}
            includeSubmitButton
            isPending={isPending}
            justEmails={false}
          />
        </div>

        <Dismissible title="Bulk Invites" id="bulk-invites">
          If you have a spreadsheet of emails you'd like to invite on Givefront,
          you can easily invite them all by pasting into any "Email Address"
          field on this form. Emails can be separated by commas, spaces, or new
          lines.
        </Dismissible>
      </form>
    </div>
  );
}
